import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Inbox as InboxIcon,
  Layers,
  RotateCcw,
  ShieldCheck,
  SlidersHorizontal,
  Users,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import {
  deleteAgentAccess,
  deleteChannelGrant,
  deleteRoleAccess,
  deleteRoleChannelGrant,
  getAccessOverview,
  previewRoleAccess,
  putAgentAccess,
  putChannelGrant,
  putRoleAccess,
  putRoleChannelGrant,
} from '../../api/endpoints';
import { ApiError } from '../../api/envelope';
import type {
  AccessAgent,
  AccessLevel,
  AccessOverview,
  AccessPolicy,
  AccessPreview,
  RoleAccessPolicy,
} from '../../api/types';
import type { ChannelType } from '../../api/types';
import { ACCESS_LEVELS } from '../../api/types';
import { Spinner } from '../../components/Spinner';
import {
  AI_LABEL,
  CAMPAIGN_HINT,
  CAMPAIGN_LABEL,
  LEVEL_HINT,
  LEVEL_LABEL,
  levelRank,
} from '../../lib/accessLevels';
import { useLoad } from '../../lib/useLoad';
import { useModuleRefresh } from './useModuleRefresh';

/**
 * Settings → Team access. Root only.
 *
 * One question per person per channel — no access, view, write or manage — plus
 * a short list of permissions that are not about a channel at all. Every value
 * shown is the RESOLVED one, with a chip saying where it came from, because the
 * model this replaced had controls whose meaning depended on a switch kept on
 * another card.
 *
 * BOTH TABS ASK THE SAME QUESTIONS, which is why they share `ChannelGrid` and
 * `PermissionRows`. A role answers for everyone in it; a person answers for
 * themselves and wins. Anything a role could not say had to be typed once per
 * head and re-typed for the next hire — so the role tab holds the channel grid
 * too, and the people tab reads the role's answer PER CHANNEL rather than
 * quoting one blanket default at a channel the role has already spoken about.
 *
 * The confirmation step is the point of the role tab, not decoration. Dropping a
 * role's default to "No access" when nobody in it holds a channel grant locks
 * that entire role out of every channel, and the first anyone hears about it is
 * a dispatcher staring at an empty screen. So a narrowing change is never saved
 * directly — it goes through `preview`, which answers how many people would be
 * left with nothing.
 *
 * ROLE DEFAULTS COMES FIRST, and is where the screen opens. Setting a role is the
 * work; opening one person is the exception to it. Landing on the roster taught
 * the opposite — that access is a per-head chore — which is how a tenant ends up
 * with the same decision typed twenty times and forgotten for the next hire.
 */

type Tab = 'roles' | 'people';

/**
 * The two questions, at the size of a decision rather than a filter chip. Each
 * says what it is FOR: which of the two an administrator wants is not obvious
 * from "People" and "Role defaults" alone, and picking wrong means either an
 * exception nobody meant or a rule that never applies.
 */
const TABS: { key: Tab; label: string; hint: string; icon: typeof Layers }[] = [
  {
    key: 'roles',
    label: 'Role defaults',
    hint: 'One answer for everyone in a dispatch role. Start here.',
    icon: Layers,
  },
  { key: 'people', label: 'People', hint: 'Exceptions, for one person only.', icon: Users },
];

/** The two halves of a person's or a role's panel — see `PanelTabs`. */
type Pane = 'channels' | 'other';
type PolicyPatch = Partial<AccessPolicy>;
/** A change to one of the role's channel cells, in the shape preview accepts. */
type ChannelPatch = { inboxId: number; name: string; level: AccessLevel | null };
type Pending = {
  role: RoleAccessPolicy;
  patch: PolicyPatch;
  channel?: ChannelPatch;
  preview: AccessPreview;
};

const VOICE_CHANNELS = ['ringcentral', 'twilio'];

/** A change that can take access away — the ones worth confirming. */
const isNarrowing = (role: RoleAccessPolicy, patch: PolicyPatch): boolean =>
  (patch.defaultLevel !== undefined && levelRank(patch.defaultLevel) < levelRank(role.defaultLevel)) ||
  (patch.contactScope === 'owned' && role.contactScope !== 'owned');

/**
 * The role's CELL for one channel, or null when it has none.
 *
 * Null and not the default, because the person tab needs to know the difference:
 * where the role said nothing, what an agent inherits is THEIR resolved default,
 * which their own override may have moved away from the role's.
 */
const roleCellLevel = (role: RoleAccessPolicy | undefined, inboxId: number): AccessLevel | null =>
  role?.grants.find((g) => g.inboxId === inboxId)?.level ?? null;

const roleCellCalls = (role: RoleAccessPolicy | undefined, inboxId: number): boolean | null =>
  role?.grants.find((g) => g.inboxId === inboxId)?.canCall ?? null;

/** What the role currently says about one channel: its cell, else its default. */
const roleLevelOn = (role: RoleAccessPolicy, inboxId: number): AccessLevel =>
  roleCellLevel(role, inboxId) ?? role.defaultLevel;

export function TeamAccessCard() {
  const overview = useLoad(getAccessOverview);
  const [tab, setTab] = useState<Tab>('roles');
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);

  const reload = overview.reload;
  useModuleRefresh(reload);

  /** Every write goes through here: one busy key, one error line, one reload. */
  const run = useCallback(
    async (key: string, fn: () => Promise<unknown>, whenFailed: string) => {
      setBusy(key);
      setError(null);
      try {
        await fn();
        reload();
      } catch (e) {
        setError(e instanceof ApiError ? e.message : whenFailed);
      } finally {
        setBusy(null);
      }
    },
    [reload],
  );

  const changeRole = async (role: RoleAccessPolicy, patch: PolicyPatch) => {
    setError(null);
    if (!isNarrowing(role, patch)) {
      return run(role.roleName, () => putRoleAccess(role.roleName, patch), 'Could not save that.');
    }
    setBusy(role.roleName);
    try {
      // Merged with the role's current values: the preview has to describe the
      // whole resulting policy, not just the field that moved.
      const result = await previewRoleAccess(role.roleName, {
        defaultLevel: patch.defaultLevel ?? role.defaultLevel,
        contactScope: patch.contactScope ?? role.contactScope,
      });
      setPending({ role, patch, preview: result });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not check that change.');
    } finally {
      setBusy(null);
    }
  };

  /**
   * One cell, three independent axes: `null` on level or calls means "follow the
   * role" for that axis alone. Voice is not a rung, so granting it must not pin a
   * level — which is exactly what writing the whole cell used to do. Ownership is
   * not a rung either and has no policy to inherit from, so it is a plain on/off
   * — and this is the only screen that sets it, the channel's own Manage page
   * having stopped asking the question twice.
   *
   * A cell left saying nothing at all is DELETED rather than kept as a row of
   * nulls: an empty row is indistinguishable from no row in effect, but would
   * keep showing up as an exception on the next load.
   */
  const writeCell = (
    agent: AccessAgent,
    inboxId: number,
    patch: { level?: AccessLevel | null; canCall?: boolean | null; isOwner?: boolean },
  ) => {
    const grant = agent.grants.find((g) => g.inboxId === inboxId);
    const level = patch.level !== undefined ? patch.level : (grant?.level ?? null);
    const canCall = patch.canCall !== undefined ? patch.canCall : (grant?.canCall ?? null);
    const isOwner = patch.isOwner !== undefined ? patch.isOwner : (grant?.isOwner ?? false);
    const empty = level === null && canCall === null && !isOwner;
    return run(
      `a${agent.dashBoardId}c${inboxId}`,
      () =>
        empty
          ? deleteChannelGrant(agent.dashBoardId, inboxId)
          : putChannelGrant(agent.dashBoardId, inboxId, patch),
      'Could not save that channel.',
    );
  };

  /** The same write, one rung up — and emptied to nothing it is deleted too. */
  const saveRoleCell = (
    role: RoleAccessPolicy,
    inboxId: number,
    patch: { level?: AccessLevel | null; canCall?: boolean | null },
  ) => {
    const grant = role.grants.find((g) => g.inboxId === inboxId);
    const level = patch.level !== undefined ? patch.level : (grant?.level ?? null);
    const canCall = patch.canCall !== undefined ? patch.canCall : (grant?.canCall ?? null);
    const empty = level === null && canCall === null;
    return run(
      `r${role.roleName}c${inboxId}`,
      () =>
        empty
          ? deleteRoleChannelGrant(role.roleName, inboxId)
          : putRoleChannelGrant(role.roleName, inboxId, patch),
      'Could not save that channel.',
    );
  };

  /**
   * Lowering a role's rung on one channel goes through the same confirmation as
   * lowering its blanket default. It usually costs one channel and not the inbox
   * — but when that channel is the only one the role reaches it is the same
   * lockout, and nobody can tell which from this screen.
   */
  const changeRoleCell = async (
    role: RoleAccessPolicy,
    channel: { id: number; name: string },
    level: AccessLevel | null,
  ) => {
    setError(null);
    const after = level ?? role.defaultLevel;
    if (levelRank(after) >= levelRank(roleLevelOn(role, channel.id))) {
      return saveRoleCell(role, channel.id, { level });
    }
    setBusy(`r${role.roleName}c${channel.id}`);
    try {
      // The whole resulting policy, as the blanket preview does — the endpoint
      // has to describe where the role ENDS UP, not the one field that moved.
      const result = await previewRoleAccess(role.roleName, {
        defaultLevel: role.defaultLevel,
        contactScope: role.contactScope,
        channel: { inboxId: channel.id, level },
      });
      setPending({
        role,
        patch: {},
        channel: { inboxId: channel.id, name: channel.name, level },
        preview: result,
      });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not check that change.');
    } finally {
      setBusy(null);
    }
  };

  if (overview.loading && !overview.data) {
    return (
      <div className="tacc-card tacc-card-centred">
        <Spinner />
      </div>
    );
  }
  if (overview.error || !overview.data) {
    return (
      <div className="tacc-card">
        <div className="tacc-error">
          {overview.error instanceof Error ? overview.error.message : 'Could not load team access.'}
        </div>
      </div>
    );
  }

  const data = overview.data;

  return (
    // `tacc` is the screenshot hook (console/scripts/capture-console.mjs) as well
    // as the style root: the notification card also carries `nset`, so a shot
    // selector of `.nset` would photograph whichever renders first.
    <div className="tacc-card nset tacc">
      <div className="tacc-head">
        <ShieldCheck size={14} />
        <span className="tacc-title">Team access</span>
      </div>
      <div className="nset-sub tacc-sub">
        Dispatch decides one thing — whether someone can open the CRM at all. Everything
        else is set here.
      </div>

      {!data.hierarchyAvailable && (
        <div className="tacc-warn">
          <AlertTriangle size={13} />
          <span>{data.hierarchyHint}</span>
        </div>
      )}
      {error && <div className="tacc-error">{error}</div>}

      <div className="tacc-tabs" role="tablist" aria-label="Team access">
        {TABS.map(({ key, label, hint, icon: Icon }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            data-tab={key}
            className={`tacc-tab${tab === key ? ' is-on' : ''}`}
            onClick={() => setTab(key)}
          >
            <Icon size={15} />
            <span className="tacc-tab-main">
              <span className="tacc-tab-label">
                {label}
                <span className="tacc-tab-count">
                  {key === 'roles' ? data.roles.length : data.agents.length}
                </span>
              </span>
              <span className="tacc-tab-hint">{hint}</span>
            </span>
          </button>
        ))}
      </div>

      {tab === 'roles' ? (
        <div className="tacc-list">
          {data.roles.map((role) => (
            <RoleRow
              key={role.roleName}
              role={role}
              channels={data.channels}
              open={openId === `r${role.roleName}`}
              busy={busy}
              onToggle={() => setOpenId(openId === `r${role.roleName}` ? null : `r${role.roleName}`)}
              onPolicy={(patch) => changeRole(role, patch)}
              onGrant={(channel, level) => changeRoleCell(role, channel, level)}
              onCalls={(inboxId, canCall) => saveRoleCell(role, inboxId, { canCall })}
              onReset={() =>
                run(role.roleName, () => deleteRoleAccess(role.roleName), 'Could not reset that role.')
              }
            />
          ))}
        </div>
      ) : (
        <div className="tacc-list">
          {data.agents.map((agent) => (
            <PersonRow
              key={agent.dashBoardId}
              agent={agent}
              role={data.roles.find((r) => r.roleName === agent.role)}
              data={data}
              open={openId === `a${agent.dashBoardId}`}
              busy={busy}
              onToggle={() =>
                setOpenId(openId === `a${agent.dashBoardId}` ? null : `a${agent.dashBoardId}`)
              }
              onPolicy={(patch) =>
                run(
                  `a${agent.dashBoardId}`,
                  () => putAgentAccess(agent.dashBoardId, patch),
                  'Could not save that.',
                )
              }
              onClearPolicy={() =>
                run(
                  `a${agent.dashBoardId}`,
                  () => deleteAgentAccess(agent.dashBoardId),
                  'Could not reset that.',
                )
              }
              onGrant={(inboxId, level) => writeCell(agent, inboxId, { level })}
              onCalls={(inboxId, canCall) => writeCell(agent, inboxId, { canCall })}
              onOwner={(inboxId, isOwner) => writeCell(agent, inboxId, { isOwner })}
            />
          ))}
        </div>
      )}

      {pending && (
        <ConfirmNarrowing
          state={pending}
          onCancel={() => setPending(null)}
          onConfirm={async () => {
            const { role, patch, channel } = pending;
            setPending(null);
            if (channel) return saveRoleCell(role, channel.inboxId, { level: channel.level });
            await run(role.roleName, () => putRoleAccess(role.roleName, patch), 'Could not save that.');
          }}
        />
      )}
    </div>
  );
}

/**
 * The per-channel grid, shared by both tabs because both tabs ask the same
 * question — a role answers for everyone in it, a person answers for themselves,
 * and the person wins.
 *
 * `inherited` is what an unset cell resolves to, computed by the caller: the
 * role's own cell for a person, the role's blanket default for a role. Quoting it
 * in the "same as" option is the whole point — a person whose role already denies
 * one line must not read "Same as role (Write)" on it.
 */
function ChannelGrid({
  channels,
  cellFor,
  inherited,
  inheritLabel,
  inheritHint,
  who,
  disabled,
  onLevel,
  onCalls,
  ownerFor,
  onOwner,
  fixedLevel,
}: {
  channels: { id: number; name: string; channelType: ChannelType }[];
  /** This row's OWN cell. `null` on an axis = it says nothing about that axis. */
  cellFor: (inboxId: number) => { level: AccessLevel | null; canCall: boolean | null };
  inherited: (inboxId: number) => { level: AccessLevel; canCall: boolean };
  /** "Same as role" / "Same as default" — where an unset cell sends the question. */
  inheritLabel: string;
  /** The same thing said under the channel name when the cell is unset. */
  inheritHint: string;
  /** Named in every aria-label, so it is clear whose grid is being read. */
  who: string;
  disabled: boolean;
  onLevel: (channel: { id: number; name: string }, level: AccessLevel | null) => void;
  onCalls: (inboxId: number, canCall: boolean | null) => void;
  /**
   * Ownership, and only the people tab passes it: a role owns no channel — it
   * cannot connect one — so the column is absent rather than disabled there.
   */
  ownerFor?: (inboxId: number) => boolean;
  onOwner?: (inboxId: number, isOwner: boolean) => void;
  /**
   * A rung this grid may not ask about — root's `manage`, which the resolver
   * answers before a row is read. Stated rather than offered as a dead dropdown:
   * a control that cannot move is a question the reader has to work the answer
   * out for. The other two columns stay live, because neither is the ladder.
   */
  fixedLevel?: AccessLevel;
}) {
  if (channels.length === 0) {
    return <div className="nset-sub">No channels are connected yet.</div>;
  }
  const anyVoice = channels.some((channel) => VOICE_CHANNELS.includes(channel.channelType));
  return (
    <div className="tacc-grid">
      {/* Column heads, because three unlabelled dropdowns in a row is a puzzle.
          Hidden on the narrow layout, where the grid stacks and each control
          carries its own label instead — see `.tacc-cell-label`. */}
      <div className="tacc-grid-head" aria-hidden="true">
        <span>Channel</span>
        <span>Can do</span>
        <span>{anyVoice ? 'Calls' : ''}</span>
        <span>{ownerFor ? 'Owner' : ''}</span>
      </div>
      {channels.map((channel) => {
        const cell = cellFor(channel.id);
        const from = inherited(channel.id);
        const voice = VOICE_CHANNELS.includes(channel.channelType);
        return (
          <div key={channel.id} className="tacc-grid-row">
            <span className="tacc-role">
              <span className="tacc-role-name">{channel.name}</span>
              <span className="tacc-role-count">
                {channel.channelType}
                {!fixedLevel && cell.level === null ? ` \u00b7 ${inheritHint}` : ''}
              </span>
            </span>
            {fixedLevel ? (
              <span className="tacc-fixed" title={LEVEL_HINT[fixedLevel]}>
                {LEVEL_LABEL[fixedLevel]}
              </span>
            ) : (
              <select
                className="nset-select"
                aria-label={`${channel.name} level for ${who}`}
                title={LEVEL_HINT[cell.level ?? from.level]}
                value={cell.level ?? ''}
                disabled={disabled}
                onChange={(e) =>
                  onLevel(channel, (e.target.value || null) as AccessLevel | null)
                }
              >
                <option value="">
                  {inheritLabel} ({LEVEL_LABEL[from.level]})
                </option>
                {ACCESS_LEVELS.map((value) => (
                  <option key={value} value={value}>
                    {LEVEL_LABEL[value]}
                  </option>
                ))}
              </select>
            )}
            {voice ? (
              <label className="tacc-calls">
                <span className="tacc-cell-label">Calls</span>
                <select
                  className="nset-select tacc-calls-select"
                  aria-label={`Calls on ${channel.name} for ${who}`}
                  title="Voice is its own axis \u2014 Manage does not imply it and View does not forbid it. Setting it here leaves the level alone."
                  value={cell.canCall === null ? '' : cell.canCall ? 'on' : 'off'}
                  disabled={disabled}
                  onChange={(e) =>
                    onCalls(channel.id, e.target.value === '' ? null : e.target.value === 'on')
                  }
                >
                  <option value="">
                    {inheritLabel} ({from.canCall ? 'on' : 'off'})
                  </option>
                  <option value="on">On</option>
                  <option value="off">Off</option>
                </select>
              </label>
            ) : (
              <span />
            )}
            {ownerFor && onOwner ? (
              <label className="tacc-calls tacc-owner">
                <span className="tacc-cell-label">Owner</span>
                <input
                  type="checkbox"
                  aria-label={`Owner of ${channel.name} for ${who}`}
                  title="New conversations here are assigned to them, and they hear about the channel whatever their notification settings say."
                  checked={ownerFor(channel.id)}
                  disabled={disabled}
                  onChange={(e) => onOwner(channel.id, e.target.checked)}
                />
              </label>
            ) : (
              <span />
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * A panel's two halves.
 *
 * An account is two unrelated questions — which channels this person or role
 * works, and the permissions that are not about a channel at all — and stacking
 * them put the second behind a grid as long as the tenant's channel list. On a
 * ten-channel tenant nobody scrolled that far, so "why can they not open
 * Campaigns" was answered by guessing.
 */
const PANES: { key: Pane; label: string; icon: typeof Layers }[] = [
  { key: 'channels', label: 'Channel access', icon: InboxIcon },
  { key: 'other', label: 'Other access', icon: SlidersHorizontal },
];

function PanelTabs({
  pane,
  onPane,
  who,
}: {
  pane: Pane;
  onPane: (next: Pane) => void;
  /** Named in the aria-label so a screen reader knows whose panel this is. */
  who: string;
}) {
  return (
    <div className="tacc-subtabs" role="tablist" aria-label={`${who} access`}>
      {PANES.map(({ key, label, icon: Icon }) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={pane === key}
          data-pane={key}
          className={`tacc-subtab${pane === key ? ' is-on' : ''}`}
          onClick={() => onPane(key)}
        >
          <Icon size={13} />
          {label}
        </button>
      ))}
    </div>
  );
}

/** The permission list both tabs share, minus the per-channel grid. */
function PermissionRows({
  policy,
  disabled,
  onPolicy,
  inheritable,
  overridden,
}: {
  policy: AccessPolicy;
  disabled: boolean;
  onPolicy: (patch: PolicyPatch) => void;
  /** People can inherit each field from their role; a role cannot. */
  inheritable: boolean;
  overridden: (keyof AccessPolicy)[];
}) {
  const mark = (field: keyof AccessPolicy) =>
    inheritable && !overridden.includes(field) ? <span className="tacc-chip">from role</span> : null;

  return (
    <div className="tacc-perms">
      <div className="tacc-perm">
        <span className="tacc-perm-name">
          Connect new channels {mark('canConnectChannels')}
          <em>They get Manage on anything they connect.</em>
        </span>
        <input
          type="checkbox"
          aria-label="Connect new channels"
          checked={policy.canConnectChannels}
          disabled={disabled}
          onChange={(e) => onPolicy({ canConnectChannels: e.target.checked })}
        />
      </div>

      <div className="tacc-perm">
        <span className="tacc-perm-name">
          Chatbot {mark('botAccess')}
          <em>Profile, knowledge and playground.</em>
        </span>
        <input
          type="checkbox"
          aria-label="Chatbot"
          checked={policy.botAccess === 'manage'}
          disabled={disabled}
          onChange={(e) => onPolicy({ botAccess: e.target.checked ? 'manage' : 'off' })}
        />
      </div>

      <div className="tacc-perm">
        <span className="tacc-perm-name">
          AI {mark('aiAccess')}
          <em>Drafting also needs Write on the channel.</em>
        </span>
        <select
          className="nset-select"
          aria-label="AI access"
          value={policy.aiAccess}
          disabled={disabled}
          onChange={(e) => onPolicy({ aiAccess: e.target.value as AccessPolicy['aiAccess'] })}
        >
          {(Object.keys(AI_LABEL) as (keyof typeof AI_LABEL)[]).map((value) => (
            <option key={value} value={value}>
              {AI_LABEL[value]}
            </option>
          ))}
        </select>
      </div>

      <div className="tacc-perm">
        <span className="tacc-perm-name">
          Campaigns {mark('campaignAccess')}
          <em>{CAMPAIGN_HINT[policy.campaignAccess]}</em>
        </span>
        <select
          className="nset-select"
          aria-label="Campaign access"
          value={policy.campaignAccess}
          disabled={disabled}
          onChange={(e) =>
            onPolicy({ campaignAccess: e.target.value as AccessPolicy['campaignAccess'] })
          }
        >
          {(Object.keys(CAMPAIGN_LABEL) as (keyof typeof CAMPAIGN_LABEL)[]).map((value) => (
            <option key={value} value={value}>
              {CAMPAIGN_LABEL[value]}
            </option>
          ))}
        </select>
      </div>

      <div className="tacc-perm">
        <span className="tacc-perm-name">
          Customers {mark('contactScope')}
          <em>Which contact records they can open.</em>
        </span>
        <select
          className="nset-select"
          aria-label="Customer scope"
          value={policy.contactScope}
          disabled={disabled}
          onChange={(e) => onPolicy({ contactScope: e.target.value as 'all' | 'owned' })}
        >
          <option value="all">All customers</option>
          <option value="owned">Only theirs</option>
        </select>
      </div>

      <div className="tacc-perm">
        <span className="tacc-perm-name">
          Assign work to others {mark('canAssignOthers')}
          <em>Off means they can only claim work themselves.</em>
        </span>
        <input
          type="checkbox"
          aria-label="Assign work to others"
          checked={policy.canAssignOthers}
          disabled={disabled}
          onChange={(e) => onPolicy({ canAssignOthers: e.target.checked })}
        />
      </div>
    </div>
  );
}

function PersonRow({
  agent,
  role,
  data,
  open,
  busy,
  onToggle,
  onPolicy,
  onClearPolicy,
  onGrant,
  onCalls,
  onOwner,
}: {
  agent: AccessAgent;
  /**
   * The role they inherit from — undefined only if dispatch reports a role the
   * overview did not list, in which case every channel falls back to their own
   * resolved default, which is what the server would answer anyway.
   */
  role: RoleAccessPolicy | undefined;
  data: AccessOverview;
  open: boolean;
  busy: string | null;
  onToggle: () => void;
  onPolicy: (patch: PolicyPatch) => void;
  onClearPolicy: () => void;
  onGrant: (inboxId: number, level: AccessLevel | null) => void;
  onCalls: (inboxId: number, canCall: boolean | null) => void;
  onOwner: (inboxId: number, isOwner: boolean) => void;
}) {
  const key = `a${agent.dashBoardId}`;
  const grantBy = new Map(agent.grants.map((g) => [g.inboxId, g]));
  const disabled = busy !== null;
  const [pane, setPane] = useState<Pane>('channels');

  return (
    <div className={`tacc-item${open ? ' is-open' : ''}`}>
      <button type="button" className="tacc-item-head" onClick={onToggle}>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="tacc-role">
          <span className="tacc-role-name">{agent.name}</span>
          <span className="tacc-role-count">
            {agent.email} · {agent.role}
          </span>
        </span>
        <span className="tacc-chips">
          {agent.isRoot && <span className="tacc-chip is-root">Administrator</span>}
          {!agent.hasCrmAccess && <span className="tacc-chip is-warn">No CRM permission</span>}
          {agent.overridden.length > 0 && (
            <span className="tacc-chip">
              {agent.overridden.length} exception{agent.overridden.length > 1 ? 's' : ''}
            </span>
          )}
        </span>
      </button>

      {open && (
        <div className="tacc-panel">
          {!agent.hasCrmAccess && (
            <div className="tacc-warn">
              <AlertTriangle size={13} />
              <span>
                Their dispatch role does not have the <code>crm</code> permission, so they
                cannot sign in. Everything below is saved and takes effect the moment it is
                granted in dispatch.
              </span>
            </div>
          )}

          {/*
            An administrator's LADDER is not a question — the resolver answers
            Manage before a row is read — so the level column states it instead of
            offering it. The other two columns stay: whose desk a call rings on
            and which channels they are told about are working arrangements, not
            access, and locking the whole panel took them away from the one person
            most likely to need them set.
          */}
          {agent.isRoot && (
            <div className="tacc-locked">
              <ShieldCheck size={15} />
              <span>
                Their dispatch role is <b>root</b>, so they are an administrator: Manage on
                every channel and every permission at its maximum, which is not set here. To
                give someone a narrower account, move them to another dispatch role. Calls
                and ownership below are still theirs to set — neither is access.
              </span>
            </div>
          )}

          {!agent.isRoot && <PanelTabs pane={pane} onPane={setPane} who={agent.name} />}

          {agent.isRoot || pane === 'channels' ? (
            <>
              <div className="nset-sub tacc-sub">
                {agent.isRoot ? (
                  <>
                    Every channel, at Manage — that part is not a question. What is left is
                    whose desk a call rings on and which lines they own. <b>Same as role</b>{' '}
                    follows {agent.role}.
                  </>
                ) : (
                  <>
                    One answer per channel. <b>Same as role</b> follows {agent.role} — and
                    quotes what that role says about <i>that</i> channel, not its default.
                  </>
                )}
              </div>
              <ChannelGrid
                channels={data.channels}
                who={agent.name}
                inheritLabel="Same as role"
                inheritHint="from role"
                disabled={disabled}
                cellFor={(id) => ({
                  level: grantBy.get(id)?.level ?? null,
                  canCall: grantBy.get(id)?.canCall ?? null,
                })}
                // Their role's answer for THIS channel, not its blanket default:
                // a role that denies one line must not read as "Write" here.
                // `effective` is the fallback because it already carries their own
                // policy override, which is what the resolver falls through to.
                inherited={(id) => ({
                  level: roleCellLevel(role, id) ?? agent.effective.defaultLevel,
                  canCall: roleCellCalls(role, id) ?? agent.effective.defaultCanCall,
                })}
                onLevel={(channel, level) => onGrant(channel.id, level)}
                onCalls={onCalls}
                ownerFor={(id) => grantBy.get(id)?.isOwner ?? false}
                onOwner={onOwner}
                fixedLevel={agent.isRoot ? 'manage' : undefined}
              />
            </>
          ) : (
            <>
              <div className="tacc-panehead">
                <span className="nset-sub tacc-sub">
                  The permissions that are not about any one channel. Each follows{' '}
                  {agent.role} until you change it here.
                </span>
                <button
                  type="button"
                  className="cbtn tacc-reset"
                  title="Clear every exception — follow the role again"
                  disabled={agent.overridden.length === 0 || busy === key}
                  onClick={onClearPolicy}
                >
                  <RotateCcw size={13} />
                  Follow the role
                </button>
              </div>
              <PermissionRows
                policy={agent.effective}
                disabled={disabled}
                onPolicy={onPolicy}
                inheritable
                overridden={agent.overridden}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function RoleRow({
  role,
  channels,
  open,
  busy,
  onToggle,
  onPolicy,
  onGrant,
  onCalls,
  onReset,
}: {
  role: RoleAccessPolicy;
  channels: { id: number; name: string; channelType: ChannelType }[];
  open: boolean;
  busy: string | null;
  onToggle: () => void;
  onPolicy: (patch: PolicyPatch) => void;
  onGrant: (channel: { id: number; name: string }, level: AccessLevel | null) => void;
  onCalls: (inboxId: number, canCall: boolean | null) => void;
  onReset: () => void;
}) {
  const disabled = busy !== null;
  const [pane, setPane] = useState<Pane>('channels');
  return (
    <div className={`tacc-item${open ? ' is-open' : ''}`}>
      <button type="button" className="tacc-item-head" onClick={onToggle}>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="tacc-role">
          <span className="tacc-role-name">{role.roleName}</span>
          <span className="tacc-role-count">
            {role.agentCount} {role.agentCount === 1 ? 'person' : 'people'}
            {role.isRoot || role.configured ? '' : ' · never edited'}
          </span>
        </span>
        <span className="tacc-chips">
          {role.isRoot ? (
            <span className="tacc-chip is-root">Administrator</span>
          ) : (
            <span className="tacc-chip">{LEVEL_LABEL[role.defaultLevel]} by default</span>
          )}
        </span>
      </button>

      {open && (
        <div className="tacc-panel">
          {/*
            Root is the dispatch role called `root`, derived on every read and
            stored nowhere: the resolver answers Manage for it before any saved
            row is looked at. So a LEVEL written here would change nothing, and
            the server refuses it rather than reporting a success that never
            happened. Calls are a different question — the resolver walks the same
            chain for root as for everyone else on that axis now — so the panel
            says what cannot move and leaves what can.
          */}
          {role.isRoot && (
            <div className="tacc-locked">
              <ShieldCheck size={15} />
              <span>
                Administrators. Manage on every channel and every permission at its maximum
                — <b>that cannot be changed here</b>, and nothing in the CRM can take the
                last administrator away. To change who is one, move them between dispatch
                roles. Calls are theirs to set: an administrator who does not work the
                phones should not have every customer ring on their desk.
              </span>
            </div>
          )}

          {!role.isRoot && <PanelTabs pane={pane} onPane={setPane} who={role.roleName} />}

          {role.isRoot || pane === 'channels' ? (
            <>
              {/*
                The baseline, as a statement rather than a row in the grid
                below it. It used to be the first line of a table of channels,
                under a heading that read "Every channel, unless set below or
                for one person" — which is accurate, and which nobody parsed:
                it looked like a channel called "Default level".
              */}
              <div className="tacc-base">
                <div className="tacc-base-top">
                  <span className="tacc-base-title">Everyone in {role.roleName} can…</span>
                  <button
                    type="button"
                    className="cbtn tacc-reset"
                    title="Back to the defaults — this clears the role's channels too"
                    disabled={!role.configured || disabled}
                    onClick={onReset}
                  >
                    <RotateCcw size={13} />
                    Reset role
                  </button>
                </div>
                <div className="tacc-base-controls">
                  <label className="tacc-base-field">
                    <span className="tacc-base-lbl">On every channel</span>
                    {role.isRoot ? (
                      <span className="tacc-fixed" title={LEVEL_HINT.manage}>
                        {LEVEL_LABEL.manage}
                      </span>
                    ) : (
                      <select
                        className="nset-select"
                        aria-label={`Default level for ${role.roleName}`}
                        value={role.defaultLevel}
                        disabled={disabled}
                        onChange={(e) => onPolicy({ defaultLevel: e.target.value as AccessLevel })}
                      >
                        {ACCESS_LEVELS.map((value) => (
                          <option key={value} value={value}>
                            {LEVEL_LABEL[value]}
                          </option>
                        ))}
                      </select>
                    )}
                  </label>
                  <label className="tacc-base-field">
                    <span className="tacc-base-lbl">Calls</span>
                    <select
                      className="nset-select"
                      aria-label={`Calls for ${role.roleName}`}
                      value={role.defaultCanCall ? 'on' : 'off'}
                      disabled={disabled}
                      onChange={(e) => onPolicy({ defaultCanCall: e.target.value === 'on' })}
                    >
                      <option value="on">On</option>
                      <option value="off">Off</option>
                    </select>
                  </label>
                </div>
                {/* What the chosen rung actually opens, in the operator's
                    words — the reason the old row carried a hint nobody could
                    see was that it sat in a table cell. */}
                <div className="tacc-base-meaning">
                  {LEVEL_HINT[role.isRoot ? 'manage' : role.defaultLevel]}
                </div>
                <div className="tacc-base-note">
                  {role.isRoot ? (
                    <>
                      The level is derived from the dispatch role and is not stored, so
                      there is nothing here to change it with. <b>Calls</b> are stored
                      like anyone else’s — this is the answer for every channel below
                      left on “Same as default”, including any connected later.
                    </>
                  ) : (
                    <>
                      This is the answer for <b>every channel below left on “Same as
                      default”</b>, including any channel connected later. Calls are their
                      own axis — Manage does not imply them, and View does not forbid them.
                    </>
                  )}
                </div>
              </div>

              <div className="tacc-section">Channels answered on their own</div>
              <div className="nset-sub tacc-sub">
                Set once for everyone in this role. Anyone given their own answer on a
                channel keeps it.
              </div>
              <ChannelGrid
                channels={channels}
                who={role.roleName}
                inheritLabel="Same as default"
                inheritHint="from the default"
                disabled={disabled}
                cellFor={(id) => {
                  const grant = role.grants.find((g) => g.inboxId === id);
                  return { level: grant?.level ?? null, canCall: grant?.canCall ?? null };
                }}
                inherited={() => ({ level: role.defaultLevel, canCall: role.defaultCanCall })}
                onLevel={onGrant}
                onCalls={onCalls}
                fixedLevel={role.isRoot ? 'manage' : undefined}
              />
            </>
          ) : (
            <>
              <div className="nset-sub tacc-sub">
                The permissions that are not about any one channel. They apply to
                everyone in {role.roleName} unless a person is given their own answer.
              </div>
              <PermissionRows
                policy={role}
                disabled={disabled}
                onPolicy={onPolicy}
                inheritable={false}
                overridden={[]}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The blast radius, before anything is saved. `losingAllAccess` is the number
 * that matters — anyone at zero channels is about to lose the inbox entirely.
 */
function ConfirmNarrowing({
  state,
  onCancel,
  onConfirm,
}: {
  state: Pending;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { role, patch, channel, preview } = state;
  const what = channel
    ? `drop ${role.roleName} to “${LEVEL_LABEL[channel.level ?? role.defaultLevel]}” on ${channel.name}`
    : patch.defaultLevel !== undefined
      ? `drop ${role.roleName} to “${LEVEL_LABEL[patch.defaultLevel]}” on every channel nobody has given them one for`
      : `restrict ${role.roleName} to only the customers assigned to them`;

  return (
    <div className="tacc-modal" role="dialog" aria-modal="true" aria-label="Confirm access change">
      <div className="tacc-modal-box">
        <div className="tacc-modal-title">
          {preview.losingAllAccess > 0 && <AlertTriangle size={16} />}
          You are about to {what}.
        </div>

        {preview.losingAllAccess > 0 ? (
          <div className="tacc-modal-alarm">
            <b>
              {preview.losingAllAccess} of {preview.agents.length}{' '}
              {preview.agents.length === 1 ? 'person' : 'people'} would be left with no channel
              at all
            </b>{' '}
            — they would open the inbox to an empty screen. Give them a channel first, then
            come back.
          </div>
        ) : (
          <div className="tacc-modal-ok">Everyone in this role keeps at least one channel.</div>
        )}

        <div className="tacc-modal-list">
          {preview.agents.map((agent) => (
            <div key={agent.dashBoardId} className="tacc-modal-row">
              <span className="tacc-modal-who">{agent.name}</span>
              <span className={`tacc-modal-count${agent.channelsAfter === 0 ? ' zero' : ''}`}>
                {agent.channelsBefore} → {agent.channelsAfter} channel
                {agent.channelsAfter === 1 ? '' : 's'}
                {agent.channelsAfter > 0 && `, ${agent.canReplyAfter} to reply in`}
              </span>
              {agent.overridden && (
                <span className="tacc-modal-tag">
                  {channel ? 'set for them on this channel' : 'has an exception'}
                </span>
              )}
            </div>
          ))}
          {preview.agents.length === 0 && (
            <div className="nset-empty">Nobody currently holds this role.</div>
          )}
        </div>

        <div className="tacc-modal-actions">
          <button type="button" className="cbtn" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="cbtn tacc-modal-go" onClick={onConfirm}>
            {preview.losingAllAccess > 0 ? 'Restrict anyway' : 'Restrict'}
          </button>
        </div>
      </div>
    </div>
  );
}
